import type { Customer, CustomerGroup, LifecycleState, SavedCard, Segment, ToastItem } from '../../components/customers/data/types';
import * as T from './customerTypes';

export const addCustomer = (customer: Customer) => ({
  type: T.CUSTOMERS_ADD as typeof T.CUSTOMERS_ADD,
  payload: customer,
});

export const updateCustomer = (id: string, updates: Partial<Customer>) => ({
  type: T.CUSTOMERS_UPDATE as typeof T.CUSTOMERS_UPDATE,
  payload: { id, updates } as T.UpdateCustomerPayload,
});

export const removeCustomer = (id: string) => ({
  type: T.CUSTOMERS_REMOVE as typeof T.CUSTOMERS_REMOVE,
  payload: id,
});

export const setFlag = (payload: T.SetFlagPayload) => ({
  type: T.CUSTOMERS_SET_FLAG as typeof T.CUSTOMERS_SET_FLAG,
  payload,
});

export const clearFlag = (payload: T.ClearFlagPayload) => ({
  type: T.CUSTOMERS_CLEAR_FLAG as typeof T.CUSTOMERS_CLEAR_FLAG,
  payload,
});

/** CON-12 — adds a revoked consent record. Consent is never granted from the portal (CON-13). */
export const recordOptOut = (payload: T.RecordOptOutPayload) => ({
  type: T.CUSTOMERS_RECORD_OPT_OUT as typeof T.CUSTOMERS_RECORD_OPT_OUT,
  payload,
});

// ─── V2: customer data operations ───
export const bulkTag = (ids: string[], tags: string[]) => ({
  type: T.CUSTOMERS_BULK_TAG as typeof T.CUSTOMERS_BULK_TAG,
  payload: { ids, tags } as T.BulkTagPayload,
});

export const setLifecycle = (ids: string[], state: LifecycleState) => ({
  type: T.CUSTOMERS_SET_LIFECYCLE as typeof T.CUSTOMERS_SET_LIFECYCLE,
  payload: { ids, state } as T.SetLifecyclePayload,
});

export const mergeCustomers = (payload: T.MergePayload) => ({
  type: T.CUSTOMERS_MERGE as typeof T.CUSTOMERS_MERGE,
  payload,
});

export const importCustomers = (customers: Customer[]) => ({
  type: T.CUSTOMERS_IMPORT as typeof T.CUSTOMERS_IMPORT,
  payload: customers,
});

// ─── V2: groups ───
export const addGroup = (group: CustomerGroup) => ({
  type: T.GROUPS_ADD as typeof T.GROUPS_ADD,
  payload: group,
});

export const updateGroup = (id: string, updates: Partial<CustomerGroup>) => ({
  type: T.GROUPS_UPDATE as typeof T.GROUPS_UPDATE,
  payload: { id, updates } as T.UpdateGroupPayload,
});

export const removeGroup = (id: string) => ({
  type: T.GROUPS_REMOVE as typeof T.GROUPS_REMOVE,
  payload: id,
});

export const setGroupMembers = (groupId: string, memberIds: string[]) => ({
  type: T.GROUPS_SET_MEMBERS as typeof T.GROUPS_SET_MEMBERS,
  payload: { groupId, memberIds } as T.SetGroupMembersPayload,
});

export const bulkAddToGroup = (groupId: string, ids: string[]) => ({
  type: T.GROUPS_BULK_ADD as typeof T.GROUPS_BULK_ADD,
  payload: { groupId, ids } as T.BulkAddToGroupPayload,
});

// ─── V2: segments ───
export const addSegment = (segment: Segment) => ({
  type: T.SEGMENTS_ADD as typeof T.SEGMENTS_ADD,
  payload: segment,
});

export const updateSegment = (id: string, updates: Partial<Segment>) => ({
  type: T.SEGMENTS_UPDATE as typeof T.SEGMENTS_UPDATE,
  payload: { id, updates } as T.UpdateSegmentPayload,
});

export const removeSegment = (id: string) => ({
  type: T.SEGMENTS_REMOVE as typeof T.SEGMENTS_REMOVE,
  payload: id,
});

// ─── P2: saved cards ───
export const addCard = (customerId: string, card: SavedCard) => ({
  type: T.CARD_ADD as typeof T.CARD_ADD,
  payload: { customerId, card } as T.AddCardPayload,
});

export const removeCard = (customerId: string, cardId: string) => ({
  type: T.CARD_REMOVE as typeof T.CARD_REMOVE,
  payload: { customerId, cardId } as T.CardRefPayload,
});

export const setDefaultCard = (customerId: string, cardId: string) => ({
  type: T.CARD_SET_DEFAULT as typeof T.CARD_SET_DEFAULT,
  payload: { customerId, cardId } as T.CardRefPayload,
});

export const toastAdd = (toast: ToastItem) => ({
  type: T.CUSTOMERS_TOAST_ADD as typeof T.CUSTOMERS_TOAST_ADD,
  payload: toast,
});

export const toastRemove = (id: string) => ({
  type: T.CUSTOMERS_TOAST_REMOVE as typeof T.CUSTOMERS_TOAST_REMOVE,
  payload: id,
});

export type CustomerAction =
  | ReturnType<typeof addCustomer>
  | ReturnType<typeof updateCustomer>
  | ReturnType<typeof removeCustomer>
  | ReturnType<typeof setFlag>
  | ReturnType<typeof clearFlag>
  | ReturnType<typeof recordOptOut>
  | ReturnType<typeof bulkTag>
  | ReturnType<typeof setLifecycle>
  | ReturnType<typeof mergeCustomers>
  | ReturnType<typeof importCustomers>
  | ReturnType<typeof addGroup>
  | ReturnType<typeof updateGroup>
  | ReturnType<typeof removeGroup>
  | ReturnType<typeof setGroupMembers>
  | ReturnType<typeof bulkAddToGroup>
  | ReturnType<typeof addSegment>
  | ReturnType<typeof updateSegment>
  | ReturnType<typeof removeSegment>
  | ReturnType<typeof addCard>
  | ReturnType<typeof removeCard>
  | ReturnType<typeof setDefaultCard>
  | ReturnType<typeof toastAdd>
  | ReturnType<typeof toastRemove>;
